import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import { supabase } from "../../lib/supabase";
import { useUserBadges } from "@/hooks/useUserBadges";
import type { Badge } from "@/hooks/useUserBadges";

interface PublicProfile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  bio: string | null;
  joined_at: string;
}

interface VisitedVenue {
  venue_id: string;
  venue_name: string;
  visited_at: string;
  category: string | null;
}

interface PublicRoute {
  id: string;
  name: string;
  description: string | null;
  stop_count: number;
  created_at: string;
}

interface RouteStopPreview {
  venue_id: string;
  venue_name: string;
  category: string;
  district: string;
  image: string;
  lat: number;
  lng: number;
  address: string;
  category_slug: string;
  order_index: number;
}

interface FollowUser {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

type ActiveTab = "overview" | "grid" | "routes" | "common" | "badges";
type FollowModal = "followers" | "following" | null;

// ─── Follow List Modal ──────────────────────────────────────────────────────
function FollowListModal({
  type,
  profileId,
  displayName,
  onClose,
}: {
  type: FollowModal;
  profileId: string;
  displayName: string;
  onClose: () => void;
}) {
  const [list, setList] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!type) return;
    setLoading(true);
    const fetchList = async () => {
      if (type === "followers") {
        const { data } = await supabase
          .from("follows")
          .select("follower_id")
          .eq("following_id", profileId);
        if (!data || data.length === 0) { setList([]); setLoading(false); return; }
        const ids = data.map((r) => r.follower_id as string);
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, username, display_name, avatar_url")
          .in("id", ids);
        setList((profiles ?? []) as FollowUser[]);
      } else {
        const { data } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", profileId);
        if (!data || data.length === 0) { setList([]); setLoading(false); return; }
        const ids = data.map((r) => r.following_id as string);
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, username, display_name, avatar_url")
          .in("id", ids);
        setList((profiles ?? []) as FollowUser[]);
      }
      setLoading(false);
    };
    fetchList();
  }, [type, profileId]);

  if (!type) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <h2 className="font-bold text-stone-900 text-base">
            {type === "followers" ? `${displayName} — Takipçiler` : `${displayName} — Takip Edilenler`}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-stone-400 hover:text-stone-700 hover:bg-stone-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 text-stone-400 text-sm">
              {type === "followers" ? "Henüz takipçi yok." : "Henüz kimse takip edilmiyor."}
            </div>
          ) : (
            list.map((u) => {
              const name = u.display_name || u.username;
              const initials = name.charAt(0).toUpperCase();
              return (
                <Link
                  key={u.id}
                  to={`/profile/${u.username}`}
                  onClick={onClose}
                  className="flex items-center gap-3 px-5 py-3.5 hover:bg-stone-50 transition-colors cursor-pointer border-b border-stone-50 last:border-0"
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt={name} className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0 border border-stone-100" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #10b981, #065f46)" }}>
                      <span className="text-white font-bold text-sm">{initials}</span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-900 text-sm truncate">{name}</p>
                    <p className="text-xs text-stone-400">@{u.username}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-stone-400 flex-shrink-0" />
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Route Preview Modal ────────────────────────────────────────────────────
function RoutePreviewModal({
  route, stops, loading, copying, copied, currentUserId, onCopy, onClose,
}: {
  route: PublicRoute;
  stops: RouteStopPreview[];
  loading: boolean;
  copying: boolean;
  copied: boolean;
  currentUserId: string | null;
  onCopy: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-stone-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-stone-900 text-base truncate">{route.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-stone-500 flex items-center gap-1">
                  <i className="ri-map-pin-line" /> {route.stop_count} durak
                </span>
                <span className="text-stone-300">·</span>
                <span className="text-xs text-stone-400">
                  {new Date(route.created_at).toLocaleDateString("tr-TR", { year: "numeric", month: "short", day: "numeric" })}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-xl cursor-pointer transition-colors flex-shrink-0">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-stone-100 rounded-xl animate-pulse" />)}
            </div>
          ) : stops.length === 0 ? (
            <div className="text-center py-10 text-stone-400 text-sm">
              <i className="ri-map-pin-line text-3xl text-stone-200 block mb-2" />
              Bu rotada durak bulunamadı.
            </div>
          ) : (
            <div className="space-y-2">
              {stops.map((stop, idx) => (
                <div key={stop.venue_id} className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-bold text-xs">{idx + 1}</span>
                  </div>
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-200">
                    <img src={stop.image} alt={stop.venue_name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-800 text-sm truncate">{stop.venue_name}</p>
                    <p className="text-xs text-stone-400 truncate">{stop.category} · {stop.district}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-stone-100 bg-stone-50">
          {!currentUserId ? (
            <Link to="/account" onClick={onClose} className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-login-box-line" /> Kopyalamak için giriş yapın
            </Link>
          ) : copied ? (
            <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-semibold">
              <i className="ri-check-double-line" /> Rotanız hesabınıza kopyalandı!
            </div>
          ) : (
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-100 cursor-pointer whitespace-nowrap transition-colors">
                Kapat
              </button>
              <button onClick={onCopy} disabled={copying || stops.length === 0} className="flex-1 py-3 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center justify-center gap-2">
                {copying ? <><i className="ri-loader-4-line animate-spin" />Kopyalanıyor...</> : <><i className="ri-file-copy-line" />Hesabıma Kopyala</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Badge Card ─────────────────────────────────────────────────────────────
function BadgeCard({ badge }: { badge: Badge }) {
  return (
    <div className={`flex flex-col items-center p-4 rounded-2xl border transition-all ${badge.earned ? "bg-white border-stone-200 hover:border-stone-300" : "bg-stone-50 border-stone-100 opacity-40"}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-2 ${badge.earned ? `bg-gradient-to-br ${badge.color}` : "bg-stone-200"}`}>
        <i className={`${badge.icon} text-2xl ${badge.earned ? "text-white" : "text-stone-400"}`} />
      </div>
      <p className={`text-xs font-bold text-center leading-tight mb-1 ${badge.earned ? "text-stone-900" : "text-stone-400"}`}>{badge.name}</p>
      <p className="text-xs text-stone-400 text-center leading-tight">{badge.description}</p>
      {badge.earned && (
        <span className="mt-1.5 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-semibold">Kazanıldı</span>
      )}
    </div>
  );
}

// ─── Overview Widget ─────────────────────────────────────────────────────────
function OverviewWidget({
  visited,
  routes,
  earnedBadges,
  followerCount,
  followingCount,
  displayName,
  isSelf,
  onTabChange,
}: {
  visited: VisitedVenue[];
  routes: PublicRoute[];
  earnedBadges: Badge[];
  followerCount: number;
  followingCount: number;
  displayName: string;
  isSelf: boolean;
  onTabChange: (tab: ActiveTab) => void;
}) {
  const recentVenues = visited.slice(0, 4);
  const recentRoutes = routes.slice(0, 3);

  const categoryMap: Record<string, number> = {};
  visited.forEach((v) => {
    if (v.category) categoryMap[v.category] = (categoryMap[v.category] ?? 0) + 1;
  });
  const topCategories = Object.entries(categoryMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  return (
    <div className="pb-16 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button
          onClick={() => onTabChange("grid")}
          className="bg-white border border-stone-200 rounded-2xl p-4 text-left hover:border-emerald-300 hover:bg-emerald-50/30 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3 group-hover:bg-emerald-200 transition-colors">
            <i className="ri-map-pin-2-line text-emerald-700 text-lg" />
          </div>
          <p className="text-2xl font-bold text-stone-900 leading-none">{visited.length}</p>
          <p className="text-xs text-stone-500 mt-1">Ziyaret Edilen Mekan</p>
        </button>

        <button
          onClick={() => onTabChange("routes")}
          className="bg-white border border-stone-200 rounded-2xl p-4 text-left hover:border-teal-300 hover:bg-teal-50/30 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center mb-3 group-hover:bg-teal-200 transition-colors">
            <i className="ri-route-line text-teal-700 text-lg" />
          </div>
          <p className="text-2xl font-bold text-stone-900 leading-none">{routes.length}</p>
          <p className="text-xs text-stone-500 mt-1">Oluşturulan Rota</p>
        </button>

        <button
          onClick={() => onTabChange("badges")}
          className="bg-white border border-stone-200 rounded-2xl p-4 text-left hover:border-amber-300 hover:bg-amber-50/30 transition-all cursor-pointer group"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
            <i className="ri-medal-line text-amber-600 text-lg" />
          </div>
          <p className="text-2xl font-bold text-stone-900 leading-none">{earnedBadges.length}</p>
          <p className="text-xs text-stone-500 mt-1">Kazanılan Rozet</p>
        </button>

        <div className="bg-white border border-stone-200 rounded-2xl p-4">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center mb-3">
            <i className="ri-user-heart-line text-rose-600 text-lg" />
          </div>
          <p className="text-2xl font-bold text-stone-900 leading-none">{followerCount}</p>
          <p className="text-xs text-stone-500 mt-1">Takipçi</p>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Recent Venues */}
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                <i className="ri-map-pin-line text-emerald-700 text-sm" />
              </div>
              <h3 className="font-bold text-stone-900 text-sm">Son Ziyaretler</h3>
            </div>
            <button
              onClick={() => onTabChange("grid")}
              className="text-xs text-emerald-700 font-semibold hover:underline cursor-pointer whitespace-nowrap"
            >
              Tümünü gör
            </button>
          </div>
          {recentVenues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-stone-400">
              <i className="ri-map-pin-line text-3xl text-stone-200 mb-2" />
              <p className="text-xs">Henüz ziyaret yok</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-50">
              {recentVenues.map((v, i) => (
                <Link
                  key={`${v.venue_id}-${i}`}
                  to={`/venue/${v.venue_id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-stone-50 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
                    <img
                      src={`https://public.readdy.ai/ai/img_res/85448a6bb38be13e5df8691766a96ec4.jpg$%7BencodeURIComponent%28v.venue_name%29%7D%20venue%20cozy%20interior%20warm%20lighting&width=80&height=80&seq=${v.venue_id}&orientation=squarish`}
                      alt={v.venue_name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-800 text-sm truncate group-hover:text-emerald-700 transition-colors">{v.venue_name}</p>
                    <p className="text-xs text-stone-400 truncate">{v.category ?? "Mekan"}</p>
                  </div>
                  <span className="text-xs text-stone-400 flex-shrink-0">
                    {new Date(v.visited_at).toLocaleDateString("tr-TR", { month: "short", day: "numeric" })}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Routes */}
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center">
                <i className="ri-route-line text-teal-700 text-sm" />
              </div>
              <h3 className="font-bold text-stone-900 text-sm">Rotalar</h3>
            </div>
            <button
              onClick={() => onTabChange("routes")}
              className="text-xs text-teal-700 font-semibold hover:underline cursor-pointer whitespace-nowrap"
            >
              Tümünü gör
            </button>
          </div>
          {recentRoutes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-stone-400">
              <i className="ri-route-line text-3xl text-stone-200 mb-2" />
              <p className="text-xs">Henüz rota yok</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-50">
              {recentRoutes.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-route-line text-teal-600 text-base" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-800 text-sm truncate">{r.name}</p>
                    <p className="text-xs text-stone-400">{r.stop_count} durak</p>
                  </div>
                  <span className="text-xs text-stone-400 flex-shrink-0">
                    {new Date(r.created_at).toLocaleDateString("tr-TR", { month: "short", day: "numeric" })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Breakdown */}
      {topCategories.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
              <i className="ri-pie-chart-line text-violet-600 text-sm" />
            </div>
            <h3 className="font-bold text-stone-900 text-sm">En Çok Ziyaret Edilen Kategoriler</h3>
          </div>
          <div className="space-y-3">
            {topCategories.map(([cat, count]) => {
              const pct = Math.round((count / visited.length) * 100);
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-stone-700 font-medium">{cat}</span>
                    <span className="text-xs text-stone-500">{count} mekan · %{pct}</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Earned Badges Preview */}
      {earnedBadges.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                <i className="ri-medal-line text-amber-600 text-sm" />
              </div>
              <h3 className="font-bold text-stone-900 text-sm">Kazanılan Rozetler</h3>
            </div>
            <button
              onClick={() => onTabChange("badges")}
              className="text-xs text-amber-600 font-semibold hover:underline cursor-pointer whitespace-nowrap"
            >
              Tümünü gör
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {earnedBadges.map((b) => (
              <div
                key={b.id}
                className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl"
                title={b.description}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center bg-gradient-to-br ${b.color}`}>
                  <i className={`${b.icon} text-white text-xs`} />
                </div>
                <span className="text-xs font-semibold text-stone-800">{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Summary */}
      <div className="bg-gradient-to-br from-emerald-700 to-teal-700 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-bar-chart-line text-emerald-200 text-lg" />
          <h3 className="font-bold text-white text-sm">
            {isSelf ? "Aktivite Özeti" : `${displayName} Hakkında`}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-white leading-none">{visited.length}</p>
            <p className="text-xs text-emerald-200 mt-1">Mekan</p>
          </div>
          <div className="text-center border-x border-white/20">
            <p className="text-2xl font-bold text-white leading-none">{routes.length}</p>
            <p className="text-xs text-emerald-200 mt-1">Rota</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white leading-none">{followerCount + followingCount}</p>
            <p className="text-xs text-emerald-200 mt-1">Bağlantı</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────
export default function PublicProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [visited, setVisited] = useState<VisitedVenue[]>([]);
  const [myVisited, setMyVisited] = useState<VisitedVenue[]>([]);
  const [commonVenues, setCommonVenues] = useState<VisitedVenue[]>([]);
  const [routes, setRoutes] = useState<PublicRoute[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>("overview");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [followModal, setFollowModal] = useState<FollowModal>(null);
  const [previewRoute, setPreviewRoute] = useState<PublicRoute | null>(null);
  const [previewStops, setPreviewStops] = useState<RouteStopPreview[]>([]);
  const [loadingStops, setLoadingStops] = useState(false);
  const [copyingRoute, setCopyingRoute] = useState(false);
  const [copiedRouteId, setCopiedRouteId] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const { badges, earnedBadges } = useUserBadges(profile?.id);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data.session?.user?.id ?? null);
    });
  }, []);

  useEffect(() => {
    if (!username) return;
    setLoadingProfile(true);
    setActiveTab("overview");
    supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url, banner_url, bio, joined_at")
      .eq("username", username)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) { setNotFound(true); }
        else { setProfile(data as PublicProfile); }
        setLoadingProfile(false);
      });
  }, [username]);

  const refreshFollowCounts = useCallback((profileId: string) => {
    supabase.from("follows").select("id", { count: "exact", head: true }).eq("following_id", profileId)
      .then(({ count }) => setFollowerCount(count ?? 0));
    supabase.from("follows").select("id", { count: "exact", head: true }).eq("follower_id", profileId)
      .then(({ count }) => setFollowingCount(count ?? 0));
  }, []);

  useEffect(() => {
    if (!profile) return;
    refreshFollowCounts(profile.id);
    if (currentUserId && currentUserId !== profile.id) {
      supabase.from("follows").select("id").eq("follower_id", currentUserId).eq("following_id", profile.id).maybeSingle()
        .then(({ data }) => setIsFollowing(!!data));
    }
  }, [profile, currentUserId, refreshFollowCounts]);

  useEffect(() => {
    if (!profile) return;
    supabase.from("visited_venues").select("venue_id, venue_name, visited_at, category")
      .eq("user_id", profile.id).order("visited_at", { ascending: false }).limit(50)
      .then(({ data }) => setVisited((data ?? []) as VisitedVenue[]));
  }, [profile]);

  useEffect(() => {
    if (!currentUserId || !profile || currentUserId === profile.id) return;
    supabase.from("visited_venues").select("venue_id, venue_name, visited_at, category")
      .eq("user_id", currentUserId).limit(200)
      .then(({ data }) => setMyVisited((data ?? []) as VisitedVenue[]));
  }, [currentUserId, profile]);

  useEffect(() => {
    if (!myVisited.length || !visited.length) { setCommonVenues([]); return; }
    const myIds = new Set(myVisited.map((v) => v.venue_id));
    setCommonVenues(visited.filter((v) => myIds.has(v.venue_id)));
  }, [myVisited, visited]);

  useEffect(() => {
    if (!profile) return;
    supabase.from("user_routes").select("id, name, description, created_at")
      .eq("user_id", profile.id).order("created_at", { ascending: false }).limit(20)
      .then(async ({ data: routeData }) => {
        if (!routeData) { setRoutes([]); return; }
        const routeIds = routeData.map((r) => r.id as string);
        const { data: stopData } = await supabase.from("route_stops").select("route_id").in("route_id", routeIds);
        const stopCounts: Record<string, number> = {};
        (stopData ?? []).forEach((s) => { const rid = s.route_id as string; stopCounts[rid] = (stopCounts[rid] ?? 0) + 1; });
        setRoutes(routeData.map((r) => ({
          id: r.id as string,
          name: r.name as string,
          description: (r.description as string | null) ?? null,
          stop_count: stopCounts[r.id as string] ?? 0,
          created_at: r.created_at as string,
        })));
      });
  }, [profile]);

  const handleFollow = async () => {
    if (!currentUserId || !profile) { navigate("/account"); return; }
    setFollowLoading(true);
    if (isFollowing) {
      await supabase.from("follows").delete().eq("follower_id", currentUserId).eq("following_id", profile.id);
      setIsFollowing(false);
      setFollowerCount((c) => c - 1);
    } else {
      await supabase.from("follows").insert({ follower_id: currentUserId, following_id: profile.id });
      setIsFollowing(true);
      setFollowerCount((c) => c + 1);
    }
    setFollowLoading(false);
  };

  const handleDM = () => {
    navigate(`/account?tab=messages&user=${profile?.username}`);
  };

  const handleOpenRoutePreview = async (route: PublicRoute) => {
    setPreviewRoute(route);
    setPreviewStops([]);
    setLoadingStops(true);
    const { data } = await supabase
      .from("route_stops")
      .select("venue_id, venue_name, category, category_slug, district, address, image, lat, lng, order_index")
      .eq("route_id", route.id)
      .order("order_index", { ascending: true });
    setPreviewStops((data ?? []) as RouteStopPreview[]);
    setLoadingStops(false);
  };

  const handleCopyRoute = async () => {
    if (!previewRoute || !currentUserId || previewStops.length === 0) return;
    setCopyingRoute(true);
    try {
      const { data: newRoute } = await supabase
        .from("user_routes")
        .insert({ user_id: currentUserId, name: `${previewRoute.name} (Kopya)`, is_tracking: false })
        .select()
        .maybeSingle();
      if (!newRoute) { setCopyingRoute(false); return; }
      const stops = previewStops.map((s, idx) => ({
        route_id: newRoute.id,
        venue_id: s.venue_id,
        venue_name: s.venue_name,
        category: s.category,
        category_slug: s.category_slug,
        district: s.district,
        address: s.address,
        image: s.image,
        lat: s.lat,
        lng: s.lng,
        completed: false,
        order_index: idx,
      }));
      if (stops.length > 0) await supabase.from("route_stops").insert(stops);
      setCopiedRouteId(previewRoute.id);
    } finally {
      setCopyingRoute(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-stone-400 text-sm">Profil yükleniyor...</p>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-user-search-line text-stone-400 text-4xl" />
            </div>
            <h2 className="font-bold text-stone-900 text-xl mb-2">Kullanıcı Bulunamadı</h2>
            <p className="text-stone-500 text-sm mb-6">
              <span className="font-medium">@{username}</span> bulunamadı.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-700 text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-emerald-800 transition-colors whitespace-nowrap">
              <i className="ri-home-line" /> Ana Sayfaya Dön
            </Link>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  const displayName = profile.display_name || profile.username;
  const initials = displayName.charAt(0).toUpperCase();
  const isSelf = currentUserId === profile.id;
  const showCommonTab = !isSelf && !!currentUserId;

  const tabs: { id: ActiveTab; label: string; icon: string; badge?: number }[] = [
    { id: "overview", label: "Özet", icon: "ri-layout-grid-line" },
    { id: "grid", label: "Mekanlar", icon: "ri-map-pin-2-line", badge: visited.length },
    { id: "routes", label: "Rotalar", icon: "ri-route-line", badge: routes.length },
    { id: "badges", label: "Rozetler", icon: "ri-medal-line", badge: earnedBadges.length },
    ...(showCommonTab ? [{ id: "common" as ActiveTab, label: "Ortak", icon: "ri-map-pin-user-line", badge: commonVenues.length }] : []),
  ];

  return (
    <div className="min-h-screen" style={{ background: "#f8f7f5" }}>
      <Navbar />

      {followModal && (
        <FollowListModal
          type={followModal}
          profileId={profile.id}
          displayName={displayName}
          onClose={() => setFollowModal(null)}
        />
      )}

      {/* Hero Banner */}
      <div className="w-full mt-16 relative" style={{ height: 240 }}>
        {profile.banner_url ? (
          <img src={profile.banner_url} alt="Profil kapak" className="w-full h-full object-cover object-top" />
        ) : (
          <div
            className="w-full h-full relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #064e3b 0%, #065f46 40%, #0f766e 70%, #134e4a 100%)" }}
          >
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1.5px, transparent 1.5px)", backgroundSize: "28px 28px" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.25) 100%)" }} />
            {/* Decorative circles */}
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-10" style={{ background: "radial-gradient(circle, white, transparent)" }} />
            <div className="absolute bottom-0 left-1/4 w-32 h-32 rounded-full opacity-5" style={{ background: "radial-gradient(circle, white, transparent)" }} />
          </div>
        )}

        {/* Overlay gradient at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(to top, #f8f7f5, transparent)" }} />

        {/* Top right actions */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {isSelf && (
            <Link
              to="/account?tab=profile"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-image-edit-line text-sm" />
              <span className="hidden sm:inline">Banner Değiştir</span>
            </Link>
          )}
          <Link
            to="/search"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-search-line text-sm" />
            <span className="hidden sm:inline">Kullanıcı Ara</span>
          </Link>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Avatar + Actions Row */}
        <div className="flex items-end justify-between -mt-14 mb-5 relative z-10">
          <div className="flex-shrink-0">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={displayName}
                className="w-24 h-24 md:w-28 md:h-28 rounded-2xl object-cover object-top border-4 border-white"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}
              />
            ) : (
              <div
                className="w-24 h-24 md:w-28 md:h-28 rounded-2xl border-4 border-white flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #065f46 100%)", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}
              >
                <span className="text-white font-bold text-3xl md:text-4xl">{initials}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pb-2 flex-wrap justify-end">
            {isSelf ? (
              <Link
                to="/account?tab=profile"
                className="flex items-center gap-1.5 px-4 py-2.5 border border-stone-300 rounded-xl text-sm font-semibold text-stone-700 bg-white hover:bg-stone-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-edit-line" />
                <span className="hidden sm:inline">Profili Düzenle</span>
                <span className="sm:hidden">Düzenle</span>
              </Link>
            ) : (
              <>
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    isFollowing
                      ? "border border-stone-300 bg-white text-stone-700 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                      : "bg-emerald-700 text-white hover:bg-emerald-800"
                  } disabled:opacity-60`}
                >
                  {followLoading
                    ? <i className="ri-loader-4-line animate-spin" />
                    : <i className={isFollowing ? "ri-user-unfollow-line" : "ri-user-follow-line"} />}
                  {isFollowing ? "Bırak" : "Takip Et"}
                </button>
                <button
                  onClick={handleDM}
                  className="flex items-center gap-1.5 px-4 py-2.5 border border-stone-300 rounded-xl text-sm font-semibold text-stone-700 bg-white hover:bg-stone-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-message-3-line" />
                  <span className="hidden sm:inline">Mesaj</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Name + Bio */}
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-bold text-stone-900 text-xl md:text-2xl leading-tight">{displayName}</h1>
            {earnedBadges.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                <i className="ri-medal-line text-xs" />
                {earnedBadges.length} rozet
              </span>
            )}
          </div>
          <p className="text-stone-500 text-sm mb-2">@{profile.username}</p>
          {profile.bio && <p className="text-stone-700 text-sm leading-relaxed max-w-lg">{profile.bio}</p>}
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="text-xs text-stone-400 flex items-center gap-1">
              <i className="ri-calendar-line" />
              {new Date(profile.joined_at).getFullYear()} yılından beri üye
            </span>
            <button
              onClick={() => setFollowModal("followers")}
              className="text-xs text-stone-600 font-semibold hover:text-emerald-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              <span className="font-bold text-stone-900">{followerCount}</span> takipçi
            </button>
            <button
              onClick={() => setFollowModal("following")}
              className="text-xs text-stone-600 font-semibold hover:text-emerald-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              <span className="font-bold text-stone-900">{followingCount}</span> takip
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`flex gap-1 mb-6 overflow-x-auto pb-1 transition-all ${scrolled ? "sticky top-16 z-20 bg-stone-50/95 backdrop-blur-sm py-2 -mx-4 px-4 md:-mx-6 md:px-6" : ""}`}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex-shrink-0 ${
                activeTab === tab.id
                  ? "bg-stone-900 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-stone-300 hover:text-stone-900"
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.id ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <OverviewWidget
            visited={visited}
            routes={routes}
            earnedBadges={earnedBadges}
            followerCount={followerCount}
            followingCount={followingCount}
            displayName={displayName}
            isSelf={isSelf}
            onTabChange={setActiveTab}
          />
        )}

        {/* Grid View */}
        {activeTab === "grid" && (
          <div className="pb-16">
            {visited.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                <i className="ri-map-pin-line text-stone-300 text-5xl" />
                <p className="text-stone-400 text-sm mt-3">Henüz ziyaret edilen mekan yok.</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-stone-400 mb-3">{visited.length} mekan ziyaret edildi</p>
                <div className="grid grid-cols-3 gap-1 md:gap-2">
                  {visited.map((v, i) => (
                    <Link
                      key={`${v.venue_id}-${i}`}
                      to={`/venue/${v.venue_id}`}
                      className="aspect-square relative overflow-hidden rounded-xl group cursor-pointer bg-stone-100 hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={`https://readdy.ai/api/search-image?query=bursa%20turkey%20restaurant%20cafe%20$%7BencodeURIComponent%28v.venue_name%29%7D%20interior%20atmosphere%20cozy&width=300&height=300&seq=${v.venue_id}&orientation=squarish`}
                        alt={v.venue_name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-2 opacity-0 group-hover:opacity-100">
                        <p className="text-white text-xs font-semibold line-clamp-2 leading-tight">{v.venue_name}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Routes View */}
        {activeTab === "routes" && (
          <div className="pb-16 space-y-3">
            {routes.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                <i className="ri-route-line text-stone-300 text-5xl" />
                <p className="text-stone-400 text-sm mt-3">Henüz oluşturulmuş rota yok.</p>
              </div>
            ) : (
              <>
                {!isSelf && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl mb-1">
                    <i className="ri-information-line text-emerald-600 text-sm" />
                    <p className="text-xs text-emerald-700">{displayName} kullanıcısının rotalarını kendi hesabınıza kopyalayabilirsiniz.</p>
                  </div>
                )}
                {routes.map((r) => (
                  <div key={r.id} className="bg-white border border-stone-200 rounded-2xl p-4 md:p-5 hover:border-stone-300 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => handleOpenRoutePreview(r)}>
                        <h3 className="font-semibold text-stone-900 mb-1 text-sm md:text-base hover:text-emerald-700 transition-colors">{r.name}</h3>
                        {r.description && <p className="text-stone-500 text-xs md:text-sm line-clamp-2">{r.description}</p>}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-xs text-stone-400">
                            <i className="ri-map-pin-line" /> {r.stop_count} durak
                          </span>
                          <span className="text-stone-300">·</span>
                          <span className="text-xs text-stone-400">
                            {new Date(r.created_at).toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleOpenRoutePreview(r)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors"
                        >
                          <i className="ri-eye-line" />
                          <span className="hidden sm:inline">Görüntüle</span>
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => handleOpenRoutePreview(r)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                              copiedRouteId === r.id
                                ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                : "bg-emerald-700 hover:bg-emerald-800 text-white"
                            }`}
                          >
                            {copiedRouteId === r.id ? (
                              <><i className="ri-check-line" />Kopyalandı</>
                            ) : (
                              <><i className="ri-file-copy-line" /><span className="hidden sm:inline">Kopyala</span></>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Common Venues View */}
        {activeTab === "common" && showCommonTab && (
          <div className="pb-16">
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3 mb-5">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-xl flex-shrink-0">
                <i className="ri-map-pin-2-line text-emerald-700 text-lg" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">
                  {commonVenues.length > 0
                    ? `${displayName} ile ${commonVenues.length} ortak mekanınız var!`
                    : `${displayName} ile henüz ortak mekanınız yok.`}
                </p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  {commonVenues.length > 0 ? "İkisinizin de ziyaret ettiği mekanlar" : "Aynı yerleri keşfetmeye başlayın"}
                </p>
              </div>
            </div>
            {commonVenues.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-2-line text-stone-300 text-3xl" />
                </div>
                <p className="text-stone-500 text-sm font-medium">Ortak mekan bulunamadı</p>
                <p className="text-stone-400 text-xs mt-1">Bursa&apos;yı birlikte keşfedin!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {commonVenues.map((v, i) => (
                  <Link
                    key={`common-${v.venue_id}-${i}`}
                    to={`/venue/${v.venue_id}`}
                    className="flex items-center gap-3 bg-white border border-stone-200 rounded-xl p-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors cursor-pointer group"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                      <img
                        src={`https://readdy.ai/api/search-image?query=bursa%20turkey%20$%7BencodeURIComponent%28v.venue_name%29%7D%20venue%20interior&width=112&height=112&seq=${v.venue_id}&orientation=squarish`}
                        alt={v.venue_name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-stone-900 text-xs md:text-sm truncate group-hover:text-emerald-800 transition-colors">{v.venue_name}</p>
                      {v.category && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">{v.category}</span>
                      )}
                      <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                        <i className="ri-check-double-line text-emerald-500" />
                        İkiniz de ziyaret
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-stone-400 group-hover:text-emerald-600 transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Badges View */}
        {activeTab === "badges" && (
          <div className="pb-16">
            {earnedBadges.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl mb-5">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-amber-500 text-base" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-800">{earnedBadges.length} rozet kazanıldı</p>
                  <p className="text-xs text-amber-600">{badges.length - earnedBadges.length} rozet daha kazanılabilir</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {badges.map((badge) => (
                <BadgeCard key={badge.id} badge={badge} />
              ))}
            </div>
          </div>
        )}

        {/* Route Preview Modal */}
        {previewRoute && (
          <RoutePreviewModal
            route={previewRoute}
            stops={previewStops}
            loading={loadingStops}
            copying={copyingRoute}
            copied={copiedRouteId === previewRoute.id}
            currentUserId={currentUserId}
            onCopy={handleCopyRoute}
            onClose={() => { setPreviewRoute(null); setPreviewStops([]); }}
          />
        )}
      </div>

      <FooterSection />
    </div>
  );
}
